import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import {
  Activity, CheckCircle2, ArrowRight, Loader2, Dumbbell, Star, Trophy, Flame, Crown, Sparkles, Zap, User, AlertTriangle, Tag, Users, Info
} from 'lucide-react';

const COPAR_PRICES = {
  basic: 74.50, plus: 74.50,
  premium: 109.90, gold: 109.90, platinum: 159.90, action: 229.90,
};

const StudentPlansPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [selecting, setSelecting] = useState(null);
  const [category, setCategory] = useState('regular');
  const [discountInfo, setDiscountInfo] = useState({ discount_amount: 0, discount_active: false });
  const [coparEnabled, setCoparEnabled] = useState(false);

  React.useEffect(() => {
    if (!authLoading && !user) navigate('/');
  }, [user, authLoading, navigate]);

  React.useEffect(() => {
    if (user) {
      axios.get(`${API}/students/my-plan`).then(r => {
        setDiscountInfo({
          discount_amount: r.data.discount_amount || 0,
          discount_active: r.data.discount_active || false
        });
      }).catch(() => {});
    }
  }, [user]);

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div></div>;
  }

  const hasDiscount = discountInfo.discount_active && discountInfo.discount_amount > 0;
  const discountAmount = discountInfo.discount_amount;

  const ELIGIBLE_FOR_DISCOUNT = ['basic', 'plus', 'premium', 'gold', 'platinum', 'action'];

  const calcDiscountedPrice = (planId, priceStr) => {
    if (!hasDiscount || !ELIGIBLE_FOR_DISCOUNT.includes(planId)) return null;
    const original = parseFloat(priceStr.replace(',', '.'));
    const final_price = Math.max(0, original - discountAmount);
    return final_price.toFixed(2).replace('.', ',');
  };

  // NOVA LINHA COMERCIAL (2026) — 3 categorias
  const dailyPlans = [
    {
      id: 'vital_start', name: 'Vital Start', price: '39,90', icon: <Zap className="w-7 h-7" />, color: 'blue',
      features: ['7 diárias/mês', 'Rede parceira completa', 'Cada check-in usa 1 diária', 'Renovação mensal automática']
    },
    {
      id: 'vital_move', name: 'Vital Move', price: '59,90', icon: <Dumbbell className="w-7 h-7" />, color: 'emerald',
      features: ['10 diárias/mês', 'Rede parceira completa', 'Cada check-in usa 1 diária', 'Renovação mensal automática']
    },
    {
      id: 'vital_session', name: 'Vital Session', price: '69,90', icon: <Star className="w-7 h-7" />, color: 'orange', popular: true,
      features: ['12 diárias/mês', 'Rede parceira completa', 'Cada check-in usa 1 diária', 'Ótimo custo-benefício']
    }
  ];

  const extrasAndRegularPlans = [
    // Ordenados por preço crescente — Check-ins Extras + Regulares unificados
    {
      id: 'basic', name: 'Vital Fit', price: '89,90', icon: <Dumbbell className="w-7 h-7" />, color: 'blue',
      features: ['+30 academias parceiras', '4 check-ins extras/mês', 'Check-in por QR Code', 'Histórico de frequência', 'Avaliações físicas trimestrais', 'Gráficos de evolução']
    },
    {
      id: 'vital_power', name: 'Vital Power', price: '99,90', icon: <Zap className="w-7 h-7" />, color: 'blue',
      features: ['5 check-ins extras/mês', 'Academia principal + extras', 'Notificação de saldo', 'Renovação automática']
    },
    {
      id: 'vital_elite', name: 'Vital Elite', price: '109,90', icon: <Trophy className="w-7 h-7" />, color: 'emerald',
      features: ['6 check-ins extras/mês', 'Academia principal + extras', 'Suporte prioritário', 'Renovação automática']
    },
    {
      id: 'plus', name: 'Vital Plus', price: '129,90', icon: <Star className="w-7 h-7" />, color: 'orange',
      features: ['+50 academias parceiras', '7 check-ins extras/mês', 'Tudo do plano Fit', 'Acesso prioritário a avaliações', 'Relatório de evolução mensal', 'Suporte prioritário']
    },
    {
      id: 'premium', name: 'Vital Premium', price: '149,90', icon: <Trophy className="w-7 h-7" />, color: 'emerald',
      features: ['+70 academias parceiras', '10 check-ins extras/mês', 'Tudo do plano Plus', 'Treinos personalizados por IA', 'Avaliações físicas mensais', 'Gerente de bem-estar dedicado']
    },
    {
      id: 'gold', name: 'Vital Gold', price: '169,90', icon: <Crown className="w-7 h-7" />, color: 'yellow', popular: true,
      features: ['+90 academias parceiras', '12 check-ins extras/mês', 'Tudo do plano Premium', 'Personal trainer virtual IA', 'Programa nutricional personalizado', 'Acesso a eventos exclusivos']
    },
    {
      id: 'vital_infinite', name: 'Vital Infinite', price: '189,90', icon: <Crown className="w-7 h-7" />, color: 'yellow',
      features: ['14 check-ins extras/mês', 'Academia principal + extras', 'Notificação de saldo baixo', 'Upgrade sem taxas']
    },
    {
      id: 'vital_platinum', name: 'Vital Platinum Ilimitado', price: '199,00', icon: <Sparkles className="w-7 h-7" />, color: 'violet',
      features: ['Check-ins ILIMITADOS', 'Sem limite numérico', 'Acesso total à rede', 'Prioridade em eventos']
    },
    {
      id: 'platinum', name: 'Vital Platinum', price: '199,90', icon: <Sparkles className="w-7 h-7" />, color: 'violet',
      features: ['Todas as academias parceiras', '14 check-ins extras/mês', 'Tudo do plano Gold', 'Acompanhamento médico trimestral', 'Acesso ilimitado a masterclasses', 'Concierge de saúde exclusivo']
    },
    {
      id: 'action', name: 'Vital Action', price: '279,90', icon: <Zap className="w-7 h-7" />, color: 'red', highlight: true,
      features: ['Todas as academias parceiras', '15 check-ins extras/mês', 'Tudo do plano Platinum', 'Treinos exclusivos de alta performance', 'Assessoria esportiva dedicada', 'Acesso VIP a eventos fitness']
    }
  ];

  const premiumPlans = [
    {
      id: 'vital_ultra', name: 'Vital Ultra', price: '239,90', icon: <Trophy className="w-7 h-7" />, color: 'emerald',
      features: ['Check-ins ILIMITADOS', '1 sessão saúde/mês (VITALPASS)', 'Fisioterapia OU Nutrição', 'Rede de profissionais parceiros']
    },
    {
      id: 'vital_action', name: 'Vital Action Premium', price: '279,90', icon: <Sparkles className="w-7 h-7" />, color: 'violet', highlight: true,
      features: ['Check-ins ILIMITADOS', '2 sessões saúde/mês (VITALPASS)', 'Fisio, Nutri OU Personal', 'Rede de profissionais completa']
    }
  ];

  const personalPlans = [
    {
      id: 'personal_plus', name: 'Vital Personal Plus', price: '469,90', icon: <Star className="w-7 h-7" />, color: 'orange',
      features: ['Check-ins ILIMITADOS na rede', '2 tokens VITALPASS profissionais', 'Sem vínculo com academia', 'Suporte prioritário']
    },
    {
      id: 'personal_gold', name: 'Vital Personal Gold', price: '579,90', icon: <Crown className="w-7 h-7" />, color: 'yellow', popular: true,
      features: ['Check-ins ILIMITADOS na rede', '3 tokens VITALPASS profissionais', 'Sem vínculo com academia', 'Personal trainer virtual IA']
    },
    {
      id: 'personal_premium', name: 'Vital Personal Premium', price: '699,90', icon: <Trophy className="w-7 h-7" />, color: 'emerald',
      features: ['Check-ins ILIMITADOS na rede', '4 tokens VITALPASS profissionais', 'Sem vínculo com academia', 'Assessoria esportiva dedicada']
    },
    {
      id: 'personal_platinum', name: 'Vital Personal Platinum', price: '890,90', icon: <Sparkles className="w-7 h-7" />, color: 'violet',
      features: ['Check-ins ILIMITADOS na rede', '5 tokens VITALPASS profissionais', 'Sem vínculo com academia', 'Concierge de saúde premium']
    }
  ];

  const plansMap = {
    daily: dailyPlans,
    premium: premiumPlans,
    regular: extrasAndRegularPlans,
    personal: personalPlans,
  };
  const plans = plansMap[category] || extrasAndRegularPlans;

  const handleSelectPlan = async (planId) => {
    setSelecting(planId);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/payments/create-checkout`, {
        plan_id: planId,
        plan_type: 'student',
        origin_url: originUrl
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar pagamento');
      setSelecting(null);
    }
  };

  const colorMap = {
    blue: { bg: 'bg-blue-600', light: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', hover: 'hover:bg-blue-700' },
    orange: { bg: 'bg-orange-500', light: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200', hover: 'hover:bg-orange-600' },
    emerald: { bg: 'bg-emerald-600', light: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', hover: 'hover:bg-emerald-700' },
    yellow: { bg: 'bg-yellow-500', light: 'bg-yellow-50', text: 'text-yellow-600', border: 'border-yellow-200', hover: 'hover:bg-yellow-600' },
    violet: { bg: 'bg-violet-600', light: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200', hover: 'hover:bg-violet-700' },
    red: { bg: 'bg-red-600', light: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', hover: 'hover:bg-red-700' },
    slate: { bg: 'bg-slate-700', light: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300', hover: 'hover:bg-slate-800' }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="student-plans-page">
      <Toaster position="top-center" />
      <header className="bg-white border-b border-slate-200 py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Activity className="w-7 h-7 text-blue-600" />
          <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Vital</span>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-50 text-emerald-600 text-sm font-medium rounded-full mb-4">
            <Flame className="w-4 h-4" /> Escolha seu plano
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Seu Passe para as Melhores Academias
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Treine onde e quando quiser. Escolha o plano que combina com a sua rotina.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex justify-center mb-10" data-testid="plan-category-tabs">
          <div className="inline-flex flex-wrap justify-center bg-white rounded-xl border border-slate-200 p-1 shadow-sm gap-1">
            <button
              onClick={() => setCategory('daily')}
              data-testid="tab-daily"
              className={`px-4 py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                category === 'daily' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Zap className="w-4 h-4 inline mr-1.5" />
              Diárias
            </button>
            <button
              onClick={() => setCategory('premium')}
              data-testid="tab-premium"
              className={`px-4 py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                category === 'premium' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Sparkles className="w-4 h-4 inline mr-1.5" />
              Premium
            </button>
            <button
              onClick={() => setCategory('regular')}
              data-testid="tab-regular"
              className={`px-4 py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                category === 'regular' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Dumbbell className="w-4 h-4 inline mr-1.5" />
              Regulares
            </button>
            <button
              onClick={() => setCategory('personal')}
              data-testid="tab-personal"
              className={`px-4 py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                category === 'personal' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <User className="w-4 h-4 inline mr-1.5" />
              Personal
            </button>
          </div>
        </div>

        {category === 'personal' && (
          <div className="max-w-2xl mx-auto mb-8 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3" data-testid="personal-plan-notice">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <strong>Planos Personal:</strong> Esses planos controlam apenas a quantidade de acessos mensais e <strong>não permitem vínculo com academia</strong>. Ideal para quem treina com personal trainer em locais variados.
            </div>
          </div>
        )}

        {hasDiscount && category === 'regular' && (
          <div className="max-w-2xl mx-auto mb-8 p-4 bg-violet-50 border border-violet-200 rounded-xl flex items-start gap-3" data-testid="discount-notice">
            <Tag className="w-5 h-5 text-violet-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-violet-800">
              <strong>Desconto aplicado pelo administrador:</strong> R$ {discountAmount.toFixed(2).replace('.', ',')} de desconto nos planos regulares.
            </div>
          </div>
        )}

        {/* Coparticipation Toggle */}
        {category === 'regular' && (
          <div className="max-w-2xl mx-auto mb-8" data-testid="copar-toggle-section">
            <div className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${coparEnabled ? 'bg-emerald-50 border-emerald-400' : 'bg-white border-slate-200 hover:border-emerald-300'}`}
              onClick={() => setCoparEnabled(!coparEnabled)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${coparEnabled ? 'bg-emerald-200' : 'bg-slate-100'}`}>
                    <Users className={`w-5 h-5 ${coparEnabled ? 'text-emerald-700' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">ADERIR COPARTICIPAÇÃO</p>
                    <p className="text-xs text-slate-500">Inclua um dependente no seu plano com valor adicional</p>
                  </div>
                </div>
                <div className={`w-12 h-6 rounded-full transition-colors flex items-center px-0.5 ${coparEnabled ? 'bg-emerald-500 justify-end' : 'bg-slate-300 justify-start'}`}>
                  <div className="w-5 h-5 rounded-full bg-white shadow-sm" />
                </div>
              </div>
              {coparEnabled && (
                <div className="mt-3 p-3 bg-emerald-100/50 rounded-lg flex items-start gap-2" data-testid="copar-info-box">
                  <Info className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-emerald-800">
                    O valor de coparticipação será adicionado ao preço do plano. Após a contratação, preencha os dados do dependente no seu <strong>Dashboard &gt; Coparticipação</strong>. A ativação final é feita pelo administrador.
                    <br /><strong>Fit/Plus:</strong> +R$ 74,50 | <strong>Premium/Gold:</strong> +R$ 109,90 | <strong>Platinum:</strong> +R$ 159,90 | <strong>Action:</strong> +R$ 229,90
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${category === 'regular' ? 'xl:grid-cols-3' : 'xl:grid-cols-3'} gap-6`}>
          {plans.map((plan) => {
            const c = colorMap[plan.color];
            const discountedPrice = calcDiscountedPrice(plan.id, plan.price);
            const coparPrice = coparEnabled && category === 'regular' ? (COPAR_PRICES[plan.id] || 0) : 0;
            const basePrice = discountedPrice ? parseFloat(discountedPrice.replace(',', '.')) : parseFloat(plan.price.replace(',', '.'));
            const totalPrice = (basePrice + coparPrice).toFixed(2).replace('.', ',');
            return (
              <div key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`relative bg-white rounded-2xl border-2 ${plan.popular ? c.border : plan.highlight ? 'border-red-300 ring-2 ring-red-100' : 'border-slate-100'} shadow-sm transition-all hover:shadow-lg hover:-translate-y-1`}
              >
                {plan.popular && (
                  <div className={`absolute -top-4 left-1/2 -translate-x-1/2 ${c.bg} text-white text-xs font-bold px-4 py-1 rounded-full`}>
                    MAIS POPULAR
                  </div>
                )}
                {plan.highlight && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-red-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                    NOVO
                  </div>
                )}
                <div className="p-6">
                  <div className={`w-14 h-14 ${c.light} rounded-xl flex items-center justify-center ${c.text} mb-4`}>
                    {plan.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {plan.name}
                  </h3>
                  {coparPrice > 0 ? (
                    <div className="mb-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        {discountedPrice ? (
                          <span className="text-sm text-slate-400 line-through">R$ {plan.price}</span>
                        ) : null}
                        <span className="text-sm text-slate-500">R$ {discountedPrice || plan.price}</span>
                        <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700" data-testid={`copar-tag-${plan.id}`}>+R$ {coparPrice.toFixed(2).replace('.', ',')} copar.</span>
                      </div>
                      <div className="flex items-baseline gap-1 mt-1">
                        <span className="text-sm text-emerald-600 font-semibold">R$</span>
                        <span className="text-4xl font-black text-emerald-600" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid={`total-price-${plan.id}`}>
                          {totalPrice}
                        </span>
                      </div>
                    </div>
                  ) : discountedPrice ? (
                    <div className="mb-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm text-slate-400 line-through">R$ {plan.price}</span>
                        <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700" data-testid={`discount-tag-${plan.id}`}>-R$ {discountAmount.toFixed(2).replace('.', ',')}</span>
                      </div>
                      <div className="flex items-baseline gap-1 mt-1">
                        <span className="text-sm text-emerald-600 font-semibold">R$</span>
                        <span className="text-4xl font-black text-emerald-600" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid={`discounted-price-${plan.id}`}>
                          {discountedPrice}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-sm text-slate-500">R$</span>
                      <span className="text-4xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        {plan.price}
                      </span>
                    </div>
                  )}
                  <p className="text-sm text-slate-500 mb-6">/mês {coparPrice > 0 ? '(com coparticipação)' : ''}</p>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 className={`w-4 h-4 ${c.text} mt-0.5 flex-shrink-0`} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleSelectPlan(plan.id)}
                    disabled={selecting !== null}
                    data-testid={`student-plan-${plan.id}`}
                    className={`w-full ${c.bg} ${c.hover} text-white`}
                  >
                    {selecting === plan.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Assinar Plano
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StudentPlansPage;
